import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
} from '@dnd-kit/core';
import {
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';

import {
	addPageModule,
	addPostRegisterHomePageModule,
	setHomepageModules,
	toggleHomepageModule,
	togglePostRegisterPreviewHomeModule,
	updateHomepageModule,
	updatePostRegisterHomeModules,
} from '../../../../../../store/actions/admin/create-event';
import { EventsState } from '../../../../../../store/types';
import { CallToAction, IPostRegisterHomeModule, LanguagesAbbr, ModuleMap, PageModule, PageModuleType } from '../../../../../../types/working-model';
import { PlaceholderSessionOption } from '../../../session/navigation/session-option';
import ModalComponent from '../../../../../general-ui/modal/modal';
import {
	GetDefaultPageModule,
} from '../../../../../../store/utils/create-event';
import { CreateEmptyPageModule, CreatePageModule, TogglePageModuleVisibility } from '../../../../../../connection/page-modules';
import CustomHTML from '../../../../../general-ui/edit-html/edit-html';
import EditModuleLayoutModal from './edit-module-layout-modal/edit-module-layout-modal';
import WaitingIndicator from '../../../../../general-ui/waiting-indicator/waiting-indicator';
import { UpdatePostRegisterPreviewModules } from '../../../../../../connection/events';
import CallToActionForm from '../../../../../general-ui/call-to-action/call-to-action';
import useCheckAndDuplicateModulesForPostRegisterHomePreview from '../../../../../../utils/use-check-and-duplicate-modules-for-post-register-home-preview';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import { sortPostRegisterHomeModules } from '../../post-register-preview/home';
import EditHeaderModal from '../../../session/editor/moduleControls/edit-header-modal';
import EditFooterModal from '../../../session/editor/moduleControls/edit-footer-modal';
import '../../../session/navigation/custom-options/custom-options.scss';
import { getHomepageDefaultLanguage } from '../../../../../../utils/get-language';
import BasicCard from '../../../../../general-ui/basic-card/basic-card';
import Switch from '../../../../../general-ui/switch/switch';
import { setScrollToModule } from '../../../../../../store/actions/admin/event-panel';
import { ParamsProps } from '../../../../../live-event/live-event';
import { useParams } from 'react-router';

interface ISortableSessionModuleProps {
	id: string;
	module: PageModule;
	toggleModule: (module: PageModule) => () => Promise<void>;
	editModule: (module: PageModule) => void;
	showSettings: boolean;
	isLandingPage?: boolean;
}

function SortableSessionModule(props: ISortableSessionModuleProps) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
	} = useSortable({ id: props.id });

	const { module, toggleModule, editModule, showSettings } = props;

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};

	const dispatch = useDispatch();

	const scrollToModule = useCallback(() => dispatch(setScrollToModule(module.type)), [dispatch, module.type]);

	return (
		<div ref={setNodeRef} style={style} className="sortable-module" onClick={scrollToModule}>
			<BasicCard className={`session-option-container ${module.type === PageModuleType.main_event_banner ? "no-toggle" : ""}`}>
				<div className="title">
					<div className="drag-handle" {...attributes} {...listeners}>
						<Icon name={ICONS.DRAG_HANDLE} size={12} color={COLORS.GRAY} />
					</div>
					{ModuleMap[module.type]}
				</div>
				<div className="session-option-right">
					{showSettings ? (
						<button className="no-style options" onClick={() => editModule(module)}>
							<Icon name={ICONS.EDIT} size={20} color={'cyan'} />
						</button>
					) : (<></>)}
					<Switch
						value={ModuleMap[module.type]}
						onClick={toggleModule(module)}
						on={module.is_on}
					/>
				</div>
			</BasicCard>
		</div>
	);
}

interface IStructureProps {
	postRegisterModules?: boolean;
	isLandingPage?: boolean;
}

const Structure: React.FC<IStructureProps> = ({ postRegisterModules = false, isLandingPage }) => {
	const {
		CreateEventReducer: {
			workingEvent,
		},
		AuthReducer: {
			token,
		},
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);
	const [addModuleOpen, setAddModuleOpen] = useState(false);
	const [blankModalOpen, setBlankModalOpen] = useState(false);
	const [workingBlankModule, setWorkingBlankModule] = useState<PageModule | null>(null);
	const [activeType, setActiveType] = useState<PageModuleType | null>(null);
	const [openLayoutModal, setOpenLayoutModal] = useState<PageModuleType | null>(null);
	const [selectedModule, setSelectedModule] = useState<PageModule | null>(null);

	const [workingCTA, setWorkingCTA] = useState<CallToAction | null>(null);
	const [workingModule, setWorkingModule] = useState<PageModule | null>(null);
	const [openHeaderModal, setOpenHeaderModal] = useState(false);
	const [openFooterModal, setOpenFooterModal] = useState(false);

	const template = workingEvent?.template.template;
	const eventName = workingEvent?.name;
	const languages = workingEvent?.homepage?.languages;

	const { language } = useParams() as ParamsProps;
	const baseLanguage = getHomepageDefaultLanguage() as LanguagesAbbr;

	useCheckAndDuplicateModulesForPostRegisterHomePreview(postRegisterModules);

	// if postRegisterModules, then use sort order (and is_on) from workingEvent.homepage.post_register_home_modules.
	// else use regular workingEvent.homepage.modules sort order
	const initModules = useCallback(() => {
		let initialModules: PageModule[] = [];
		if (!workingEvent?.homepage) return [];
		if (postRegisterModules) {
			initialModules = [...sortPostRegisterHomeModules(workingEvent.homepage.modules, workingEvent.homepage.post_register_home_modules)];
		} else {
			initialModules = [...workingEvent!.homepage!.modules];
		}
		return initialModules;
	}, [postRegisterModules, workingEvent]);

	function toggleModule(module: PageModule) {
		return async () => {
			if (!module.id || !token) return;
			const updatedIsOn = !module.is_on;
			// update redux
			if (postRegisterModules) {
				dispatch(togglePostRegisterPreviewHomeModule(module.id, updatedIsOn));
			} else {
				dispatch(toggleHomepageModule(module));
			}
			try {
				// update database
				await TogglePageModuleVisibility(token, module.id, updatedIsOn);
			} catch (e) {
				console.error(e?.response || e);
			}
		};
	}

	async function handleDragEnd(event: DragEndEvent) {
		if (!event.over) return;
		const _modules = [...initModules()];
		const startIndex = _modules.findIndex((module: PageModule) => Number(module.id) === Number(event.active.id));
		const newIndex = _modules.findIndex((module: PageModule) => Number(module.id) === Number(event?.over?.id));

		const [module] = _modules.splice(startIndex, 1);
		_modules.splice(newIndex, 0, module);
		if (startIndex === newIndex) return;
		if (postRegisterModules) {
			const homepage = workingEvent?.homepage?.homepage;
			if (!token || !homepage) return;
			// update in redux
			const udpatedPostRegisterModules = _modules.reduce((accum: IPostRegisterHomeModule[], module: PageModule) => {
				if (!module.id) return accum;
				return [
					...accum,
					{ is_on: module.is_on, module_id: module.id }
				];
			}, []);
			dispatch(updatePostRegisterHomeModules(udpatedPostRegisterModules));
			// update in db
			try {
				await UpdatePostRegisterPreviewModules(homepage, token, udpatedPostRegisterModules);
			} catch (e) {
				console.error(e?.response || e);
			}
		} else {
			dispatch(setHomepageModules(_modules));
		}
	}

	function addModule() {
		setAddModuleOpen(true);
	}

	function closeModuleModal() {
		setAddModuleOpen(false);
	}

	function selectModule(type: PageModuleType) {
		return () => {
			setActiveType(type);
		};
	}

	async function createBlank() {
		if (activeType) {
			const module = GetDefaultPageModule(activeType);
			module.content.html = "";
			setAddModuleOpen(false);
			setWorkingBlankModule(module);
			setBlankModalOpen(true);
		}
	}

	async function finishedEditingBlank() {
		if (workingBlankModule) {
			if (workingBlankModule.id) {
				dispatch(
					updateHomepageModule(workingBlankModule)
				);
			} else {
				const newModule = await CreateEmptyPageModule(token!, workingBlankModule);
				// else update the working event modules as normal in database and redux:
				dispatch(addPageModule(newModule));
				if (postRegisterModules) {
					if (workingEvent?.homepage?.homepage && token) {
						// add the newModule data to the post_register_home_modules list in database and the module in redux
						dispatch(addPostRegisterHomePageModule(token, workingEvent.homepage.homepage, newModule));
					}
				}
			}
		}

		closeBlankModule();
	}

	function updateWorkingBlankModule(code: string) {
		setWorkingBlankModule((module: PageModule | null) => {
			if (!module) return null;
			return {
				...module,
				content: {
					html: code
				}
			};
		});
	}

	const setCallToActionButton = useCallback(() => {
		if (!workingCTA || !workingModule) return;
		dispatch(
			updateHomepageModule({
				...workingModule,
				content: {
					...workingModule.content,
					callToAction: workingCTA
				}
			})
		);
	}, [workingCTA, dispatch, workingModule]);

	function closeBlankModule() {
		setBlankModalOpen(false);
	}

	async function createModule() {
		if (!token || !template || !eventName || !languages || !activeType) return;

		const baseLanguage = getHomepageDefaultLanguage();

		const newModule = await CreatePageModule(token, {
			template,
			eventName,
			languages,
			type: activeType,
			baseLanguage
		});

		// update the working event modules as normal in database and redux:
		dispatch(addPageModule(newModule));
		if (postRegisterModules) {
			// add the module info to the post_register_home_modules list in database and redux
			if (workingEvent?.homepage?.homepage) {
				dispatch(addPostRegisterHomePageModule(token, workingEvent.homepage.homepage, newModule));
			}
		}
		setAddModuleOpen(false);
	}

	const editableContent = useMemo(() => {
		switch (workingModule?.type) {
			case PageModuleType.callout: {
				if (!workingCTA) return undefined;
				return {
					jsx: <CallToActionForm
						workingCTA={workingCTA}
						onWorkingCTAChange={setWorkingCTA}
						language={language}
						baseLanguage={baseLanguage}
					/>,
					onDone: setCallToActionButton
				};
			}
			default: return undefined;
		}
	}, [workingModule, workingCTA, setCallToActionButton, baseLanguage, language]);

	const section = useMemo(() => {
		switch (activeType) {
			case PageModuleType.speakers: {
				return <button disabled={!activeType} className="primary" onClick={createModule}>Pick Speakers</button>;
			}
			case PageModuleType.agenda: {
				return <button disabled={!activeType} className="primary" onClick={createModule}>Pick Agenda</button>;
			}
			case PageModuleType.about: {
				return <button disabled={!activeType} className="primary" onClick={createModule}>Pick About</button>;
			}
			case PageModuleType.products: {
				return <button disabled={!activeType} className="primary" onClick={createModule}>Pick Products</button>;
			}
			case PageModuleType.videos: {
				return <button disabled={!activeType} className="primary" onClick={createModule}>Pick Videos</button>;
			}
			case PageModuleType.sponsors: {
				return <button disabled={!activeType} className="primary" onClick={createModule}>Pick Sponsors</button>;
			}
			case PageModuleType.callout: {
				return <button disabled={!activeType} className="primary" onClick={createModule}>Pick Callout</button>;
			}
			case PageModuleType.blank: {
				return <button disabled={!activeType} className="primary" onClick={createBlank}>Pick Blank</button>;
			}
			default: return <button disabled={!activeType} className="primary">Pick Layout</button>;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeType]);


	function editModule(module: PageModule) {
		//reseting these values so they dont carry over from a previous module
		setWorkingModule(null);
		setWorkingCTA(null);

		if (module.type === PageModuleType.blank) {
			setWorkingBlankModule(module);
			setBlankModalOpen(true);
			setOpenLayoutModal(null);
		} else {
			if (module) {
				setSelectedModule(module || 0);
				switch (module.type) {
					case PageModuleType.main_event_banner:
						setOpenLayoutModal(PageModuleType.main_event_banner);
						break;
					case PageModuleType.speakers:
						setOpenLayoutModal(PageModuleType.speakers);
						break;
					case PageModuleType.about:
						setOpenLayoutModal(PageModuleType.about);
						break;
					case PageModuleType.agenda:
						setOpenLayoutModal(PageModuleType.agenda);
						break;
					case PageModuleType.products:
						setOpenLayoutModal(PageModuleType.products);
						break;
					case PageModuleType.sponsors:
						setOpenLayoutModal(PageModuleType.sponsors);
						break;
					case PageModuleType.videos:
						setOpenLayoutModal(PageModuleType.videos);
						break;
					case PageModuleType.callout:
						setOpenLayoutModal(PageModuleType.callout);
						setWorkingModule(module);
						setWorkingCTA(module.content.callToAction);
						break;
					default:
						setOpenLayoutModal(PageModuleType.main_event_banner);
				}
			}
		}
	}

	return (
		initModules() ? (
			<div className="structure-container">
				<div className="structure-list">
					{/* display header/footer on landing page panel only */}
					{isLandingPage && <PlaceholderSessionOption label="Navigation" onEditIconClick={() => setOpenHeaderModal(true)} />}
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
						modifiers={[restrictToParentElement]}
						autoScroll={false}
					>
						<SortableContext
							items={initModules().map((module: PageModule) => String(module.id))}
							strategy={verticalListSortingStrategy}
						>
							{initModules()!.map((module: PageModule, index: number) => (
								<SortableSessionModule
									key={index}
									module={module}
									toggleModule={toggleModule}
									editModule={editModule}
									id={String(module.id)}
									showSettings={postRegisterModules ? false : true}
									isLandingPage={isLandingPage}
								/>
							))}
						</SortableContext>
					</DndContext>
					{isLandingPage && <PlaceholderSessionOption label="Footer" onEditIconClick={() => setOpenFooterModal(true)} />}
					{postRegisterModules ? null : (
						<button className="no-style session-option-container add-custom-section" onClick={addModule}>
							<Icon name={ICONS.ADD} size={16} color={COLORS.CYAN} />
							Custom Section
						</button>
					)}
				</div>

				<ModalComponent
					cancellable={true}
					closeable={false}
					open={addModuleOpen}
					onRequestClose={closeModuleModal}
					title={"Select Layout"}
				>
					<div className="custom-section-modal">
						<div className="custom-section-select">
							<ul>
								{/* <li onClick={selectModule(PageModuleType.main_event_banner)} className={classNames({ active: activeType === PageModuleType.main_event_banner })}><span>Main Event Banner</span></li> */}
								<li className={classNames("basic-card", { active: activeType === PageModuleType.speakers })}><button onClick={selectModule(PageModuleType.speakers)} className="custom-section">Speakers</button></li>
								<li className={classNames("basic-card", { active: activeType === PageModuleType.agenda })}><button onClick={selectModule(PageModuleType.agenda)} className="custom-section">Agenda</button></li>
								<li className={classNames("basic-card", { active: activeType === PageModuleType.about })}><button onClick={selectModule(PageModuleType.about)} className="custom-section">About</button></li>
								<li className={classNames("basic-card", { active: activeType === PageModuleType.products })}><button onClick={selectModule(PageModuleType.products)} className="custom-section">Products</button></li>
								<li className={classNames("basic-card", { active: activeType === PageModuleType.videos })}><button onClick={selectModule(PageModuleType.videos)} className="custom-section">Video</button></li>
								<li className={classNames("basic-card", { active: activeType === PageModuleType.sponsors })}><button onClick={selectModule(PageModuleType.sponsors)} className="custom-section">Sponsors</button></li>
								<li className={classNames("basic-card", { active: activeType === PageModuleType.callout })}><button onClick={selectModule(PageModuleType.callout)} className="custom-section">Callout</button></li>
								<li className={classNames("basic-card", { active: activeType === PageModuleType.blank })}><button onClick={selectModule(PageModuleType.blank)} className="custom-section">+ Blank Section</button></li>
							</ul>
						</div>
						<div className="custom-section-layouts">
							<button onClick={closeModuleModal} style={{ marginRight: 16 }}>Cancel</button>
							{section}
						</div>
					</div>
				</ModalComponent>

				<ModalComponent
					cancellable={true}
					closeable={false}
					open={blankModalOpen}
					onRequestClose={closeBlankModule}
					title={"Blank Module"}
					footer={(
						<>
							<button onClick={closeBlankModule} >Cancel</button>
							<button onClick={finishedEditingBlank} className="primary">Done</button>
						</>
					)}
				>
					<div>
						<label className="subtitle">Add custom HTML Code</label>

						<CustomHTML
							defaultValue={workingBlankModule?.content.html ?? ''}
							onChange={updateWorkingBlankModule}
						/>
					</div>
				</ModalComponent>

				{
					selectedModule && (
						<EditModuleLayoutModal
							/* openLayoutModal is a number, and since zero is falsey we need to check for its existence as well */
							editModule={editableContent}
							open={!!openLayoutModal || openLayoutModal === PageModuleType.main_event_banner}
							setOpenLayoutModal={setOpenLayoutModal}
							pageModuleType={openLayoutModal}
							selectedModule={selectedModule?.id}
							title={ModuleMap[selectedModule!.type]}
						/>
					)
				}
				<EditHeaderModal
					open={openHeaderModal}
					closeModal={() => setOpenHeaderModal(false)}
					baseLanguage={baseLanguage}
					language={language}
				/>
				<EditFooterModal
					open={openFooterModal}
					closeModal={() => setOpenFooterModal(false)}
					baseLanguage={baseLanguage}
					language={language}
				/>
			</div>
		) : (
			<WaitingIndicator />
		)
	);
};

export default Structure;
